/**
 * QaFeedbackFormModal — QA 피드백 제출 폼 (제목·설명 → 지정 채널 Tasks 카드)
 *
 * 흐름: 캡처 → 주석 편집기 → 이 폼(제목·설명) → 제출 → `[QA-F###]` 태스크 카드 생성 → 토스트+링크.
 * IS_QA_ELIGIBLE 게이트 뒤에서만 렌더 → 운영 빌드에서 DCE 제거.
 */
import { useState, useMemo, useEffect } from 'react';
import { Trans, t } from '@lingui/macro';
import { Loader2, Bug } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { ToastAction } from '../../components/ui/toast';
import { useToast } from '../../components/ui/use-toast';
import { useNavigate } from 'react-router-dom';
import type { FeedbackEnvironment, FeedbackDiagnostics } from '../../models/Feedback';
import { submitQaFeedback, QaFeedbackError } from '../../services/QaFeedbackService';

interface QaFeedbackFormModalProps {
  open: boolean;
  annotatedBlob: Blob | null;
  environment: FeedbackEnvironment;
  diagnostics: FeedbackDiagnostics;
  onClose: () => void;
  /** 제출 성공 후 흐름 종료 */
  onSubmitted: () => void;
}

export const QaFeedbackFormModal: React.FC<QaFeedbackFormModalProps> = ({
  open, annotatedBlob, environment, diagnostics, onClose, onSubmitted,
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const previewUrl = useMemo(() => (annotatedBlob ? URL.createObjectURL(annotatedBlob) : null), [annotatedBlob]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  // 열릴 때 폼 초기화
  useEffect(() => {
    if (open) { setTitle(''); setDescription(''); }
  }, [open]);

  const errorMessage = (reason: QaFeedbackError['reason']): string => {
    switch (reason) {
      case 'no-board':
        return t`This channel has no Tasks board yet. Ask the channel owner to activate Tasks.`;
      case 'not-member':
        return t`You are not a member of the QA feedback channel, so the card could not be created. Please ask to be invited.`;
      default:
        return t`Failed to submit QA feedback`;
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast({ variant: 'destructive', title: t`Please enter a title` });
      return;
    }
    if (!annotatedBlob) return;
    setSubmitting(true);
    try {
      const result = await submitQaFeedback({
        annotatedBlob, title, description, environment, diagnostics,
      });
      toast({
        title: t`QA feedback submitted`,
        description: result.title,
        action: result.target === 'prod' ? (
          // 운영 앱 카드 — 절대 URL. window.open 은 토스트가 닫히며 사용자 제스처
          // 컨텍스트를 잃어 팝업 차단에 걸리므로, 앵커 클릭(차단 대상 아님)으로 연다.
          <ToastAction altText={t`Open card`} asChild>
            <a href={result.link} target="_blank" rel="noopener noreferrer">
              <Trans>Open card</Trans>
            </a>
          </ToastAction>
        ) : (
          <ToastAction altText={t`Open card`} onClick={() => navigate(result.link)}>
            <Trans>Open card</Trans>
          </ToastAction>
        ),
      });
      onSubmitted();
    } catch (err) {
      console.error('[QaFeedbackFormModal] submit failed', err);
      const reason = err instanceof QaFeedbackError ? err.reason : 'unknown';
      toast({ variant: 'destructive', title: errorMessage(reason) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !submitting) onClose(); }}>
      <DialogContent className="max-w-[540px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="w-5 h-5 text-destructive" />
            <Trans>Report a bug to QA</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>A task card will be created in the QA feedback channel.</Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* 주석 이미지 미리보기 */}
          {previewUrl && (
            <div className="flex items-center justify-center bg-muted/30 p-2 rounded-lg border border-border">
              <img src={previewUrl} alt="" className="max-h-[300px] rounded-md object-contain" />
            </div>
          )}

          {/* 제목 */}
          <div className="space-y-1">
            <Label htmlFor="qa-title"><Trans>Title</Trans> *</Label>
            <Input
              id="qa-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t`Short summary of the issue`}
              autoFocus
            />
          </div>

          {/* 설명 */}
          <div className="space-y-1">
            <Label htmlFor="qa-desc"><Trans>Description</Trans></Label>
            <Textarea
              id="qa-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t`What happened, what you expected, steps to reproduce...`}
              rows={5}
            />
          </div>

          <p className="text-meta text-muted-foreground">
            <Trans>Version, URL, and diagnostics are attached automatically.</Trans>
          </p>
        </div>

        <DialogFooter className="mt-4 flex flex-row justify-end space-x-2">
          <Button variant="outline" onClick={onClose} disabled={submitting} className="mr-auto">
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !title.trim()}>
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <Trans>Submit</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
